import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  Scale,
  Users,
  Cloud,
  ClipboardCheck,
  ArrowRight,
  CheckCircle,
  ChevronRight,
  Landmark,
  Cpu,
  Stethoscope,
  Factory,
  Briefcase,
  Building2
} from 'lucide-react';
import { companyInfo, whatWeDo, whyUs, industries, faqData } from '../data/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import HeroGraphic from '../components/graphics/HeroGraphic';
import NetworkAnimation from '../components/graphics/NetworkAnimation';
import ParallaxSection from '../components/graphics/ParallaxSection';
import FloatingElements from '../components/graphics/FloatingElements';
import SEO from '../components/SEO';

const iconMap = {
  Shield,
  Scale,
  Users,
  Cloud,
  ClipboardCheck,
  Landmark,
  Cpu,
  Stethoscope,
  Factory,
  Briefcase,
  Building2
};

const HomePage = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const navigate = useNavigate();

  const handleServiceClick = (link) => {
    navigate(link);
  };

  return (
    <div className="bg-slate-950">
      <SEO 
        title="CORE IT Group"
        description="CORE IT Group delivers practical, security-focused IT, cybersecurity, and risk management services that help organizations operate securely, comply with regulations, and scale with confidence."
        keywords="cybersecurity services, IT risk management, GRC consulting, TPRM, SOC 2 readiness, ISO 27001, NIST compliance, cloud security, vendor risk management"
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.9),rgba(15,23,42,0.95)),url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23334155%22 fill-opacity=%220.1%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
          <FloatingElements />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-8 animate-fade-in">
                <Shield className="w-4 h-4" />
                Trusted Cybersecurity Partner
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Practical, Security-Focused
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                  IT & Cybersecurity Services
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-400 leading-relaxed mb-10">
                {companyInfo.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 group">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600">
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Graphic */}
            <div className="hidden lg:block">
              <HeroGraphic />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-slate-500 rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <ParallaxSection className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Our Expertise</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">What We Do</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive cybersecurity and risk management services tailored to your organization's needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatWeDo.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={item.id}
                  className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group cursor-pointer interactive-card hover-reveal"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onMouseEnter={() => setHoveredService(item.id)}
                  onMouseLeave={() => setHoveredService(null)}
                  onClick={() => handleServiceClick(item.link)}
                >
                  <CardContent className="p-6 relative">
                    <div className="relative w-12 h-12 mb-4">
                      <div className={`absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-teal-500/30 rounded-lg blur-lg transition-all duration-300 ${hoveredService === item.id ? 'opacity-100 scale-125' : 'opacity-50'}`} />
                      <div className="relative w-full h-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        {IconComponent && <IconComponent className={`w-6 h-6 transition-colors duration-300 ${hoveredService === item.id ? 'text-teal-400' : 'text-cyan-400'}`} />}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                      {item.description}
                    </p>
                    <div className="flex items-center text-cyan-400 text-sm font-medium group-hover:text-teal-400 transition-colors">
                      Learn more
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </ParallaxSection>

      {/* Why CORE IT Group Section */}
      <section className="py-24 bg-slate-900/50 relative">
        {/* Interactive Network Background */}
        <NetworkAnimation />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Why Choose Us</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Why CORE IT Group?</h2>
              <p className="text-slate-400 mb-8">
                We bridge the gap between technical security controls and real business risk, delivering outcomes that matter.
              </p>

              <div className="space-y-6">
                {whyUs.map((item, index) => (
                  <div 
                    key={item.id} 
                    className="flex gap-4 group hover:translate-x-2 transition-transform duration-300"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                      <CheckCircle className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1 group-hover:text-cyan-400 transition-colors">{item.title}</h3>
                      <p className="text-slate-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/about" className="mt-8 inline-block">
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500">
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 rounded-2xl blur-2xl animate-pulse-slow" />
              <div className="relative bg-slate-900 border border-slate-800 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '100+', label: 'Clients Served', color: 'cyan' },
                    { value: '98%', label: 'Client Satisfaction', color: 'teal' },
                    { value: '10+', label: 'Years Experience', color: 'cyan' },
                    { value: '24/7', label: 'Support Available', color: 'teal' }
                  ].map((stat, index) => (
                    <div 
                      key={index}
                      className="text-center p-6 bg-slate-800/50 rounded-xl group hover:bg-slate-800 transition-all duration-300 cursor-default interactive-card"
                    >
                      <div className={`text-4xl font-bold mb-2 ${stat.color === 'cyan' ? 'text-cyan-400' : 'text-teal-400'} group-hover:scale-110 transition-transform duration-300`}>
                        {stat.value}
                      </div>
                      <div className="text-slate-400 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <ParallaxSection className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Industries</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Industries We Serve</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We bring specialized expertise across multiple sectors, understanding the unique challenges each industry faces.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((industry, index) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <div
                  key={industry.id}
                  className="group p-6 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-cyan-500/50 hover:bg-slate-900 transition-all duration-300 text-center interactive-card cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative w-12 h-12 mx-auto mb-4">
                    <div className="absolute inset-0 bg-cyan-500/20 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative w-full h-full bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-cyan-500/10 transition-all duration-300 group-hover:scale-110">
                      {IconComponent && <IconComponent className="w-6 h-6 text-slate-400 group-hover:text-cyan-400 transition-colors" />}
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 group-hover:text-white transition-colors font-medium">
                    {industry.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </ParallaxSection>

      {/* FAQ Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`faq-${faq.id}`}
                className="bg-slate-900 border border-slate-800 rounded-xl px-6 data-[state=open]:border-cyan-500/50"
              >
                <AccordionTrigger className="text-white hover:text-cyan-400 hover:no-underline text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700">
            <div className="absolute inset-0">
              <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
            </div>
            
            <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready to Strengthen Your Security Posture?
              </h2>
              <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
                Let's discuss how we can help your organization reduce risk, achieve compliance, and build trust with your stakeholders.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
                  Contact Us Today
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
